export type PinnedHydrationContext = {
  loadedMessageIds: string[];
  attemptsByMessageId: Map<string, number>;
  maxAttempts?: number;
};

/** Dernière mutation d'épinglage reçue en temps réel pour un message. */
export type PinMutation = 'pin' | 'unpin';

/**
 * Le back publie l'événement Ably dans la même transaction que l'épinglage : une hydratation
 * déclenchée aussitôt peut lire l'état d'avant le commit et revenir sans le message. Une réponse
 * en échec réseau produit le même vide. Il faut donc pouvoir retenter, mais un nombre borné de
 * fois, sinon un id réellement introuvable relance sans fin.
 */
export const PINNED_HYDRATION_MAX_ATTEMPTS = 3;

/** Laisse au back le temps de committer avant la tentative suivante. */
export const PINNED_HYDRATION_RETRY_DELAY_MS = 1500;

/**
 * L'événement Ably de pin ne transporte que l'id du message. Un message posté avant l'entrée du
 * spectateur puis épinglé pendant le live n'est dans aucune liste locale : il faut alors recharger
 * l'historique épinglé.
 */
export function shouldHydratePinnedMessage(
  messageId: string,
  context: PinnedHydrationContext,
): boolean {
  if (context.loadedMessageIds.includes(messageId)) return false;
  const attempts = context.attemptsByMessageId.get(messageId) ?? 0;
  return attempts < (context.maxAttempts ?? PINNED_HYDRATION_MAX_ATTEMPTS);
}

/** Une tentative de plus pour ce message, à poser avant l'appel réseau. */
export function recordPinnedHydrationAttempt(
  messageId: string,
  attemptsByMessageId: Map<string, number>,
): void {
  attemptsByMessageId.set(messageId, (attemptsByMessageId.get(messageId) ?? 0) + 1);
}

/** Attente entre deux tentatives d'hydratation. */
export function waitBeforePinnedHydrationRetry(
  delayMs: number = PINNED_HYDRATION_RETRY_DELAY_MS,
): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, delayMs);
  });
}

/** L'historique renvoyé porte-t-il enfin le message épinglé qu'on cherchait ? */
export function historyContainsMessageId(
  dtos: { id: number | string }[],
  messageId: string,
): boolean {
  return dtos.some((dto) => String(dto.id) === messageId);
}

/**
 * L'historique est paginé et cacheable côté serveur : sa réponse peut être plus ancienne que les
 * événements Ably déjà appliqués. Un message désépinglé entre-temps ne doit pas être réépinglé par
 * l'arrivée tardive du snapshot. Un `pin` reçu depuis lève le masque.
 */
export function filterPinnedIdsFromHistory(
  historyPinnedIds: string[],
  realtimePinMutations: Map<string, PinMutation>,
): string[] {
  return historyPinnedIds.filter((messageId) => realtimePinMutations.get(messageId) !== 'unpin');
}

/**
 * Ids épinglés à retenir d'une réponse d'historique, quelle que soit la route (spectateur, chat
 * staff, questions staff) : toutes portent le même champ `pinned` et subissent le même risque de
 * snapshot périmé.
 */
export function selectPinnedIdsFromHistory(
  dtos: { id: number | string; pinned?: boolean }[],
  realtimePinMutations: Map<string, PinMutation>,
): string[] {
  return filterPinnedIdsFromHistory(
    dtos.filter((dto) => dto.pinned === true).map((dto) => String(dto.id)),
    realtimePinMutations,
  );
}
